import React from 'react';
import { FaShieldAlt, FaLock, FaEye, FaFileAlt } from 'react-icons/fa';

const DataProtection = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">Data Protection</h1>
            <p className="text-xl md:text-2xl text-purple-100 max-w-3xl mx-auto">
              Comprehensive data protection measures to keep your information secure
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-600 mb-8">
              <strong>Last updated:</strong> {new Date().toLocaleDateString()}
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <FaShieldAlt className="h-6 w-6 mr-2 text-purple-600" />
                Our Commitment to Data Protection
              </h2>
              <p className="text-gray-700 leading-relaxed">
                At Jhonks, we are committed to protecting your personal data and ensuring compliance with applicable data protection laws. 
                This document outlines our comprehensive data protection measures and your rights regarding your personal information.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <FaLock className="h-6 w-6 mr-2 text-purple-600" />
                Technical Security Measures
              </h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Encryption</h3>
              <ul className="list-disc pl-6 mb-4 text-gray-700">
                <li>All data is encrypted in transit using TLS 1.3</li>
                <li>Data at rest is encrypted using AES-256 encryption</li>
                <li>Database connections are secured with SSL certificates</li>
                <li>API communications are protected with OAuth 2.0</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Access Controls</h3>
              <ul className="list-disc pl-6 mb-4 text-gray-700">
                <li>Multi-factor authentication for all admin accounts</li>
                <li>Role-based access control (RBAC) system</li>
                <li>Regular access reviews and audits</li>
                <li>Principle of least privilege access</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Infrastructure Security</h3>
              <ul className="list-disc pl-6 text-gray-700">
                <li>Secure cloud infrastructure with regular updates</li>
                <li>Network segmentation and firewalls</li>
                <li>Intrusion detection and prevention systems</li>
                <li>Regular security vulnerability assessments</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <FaEye className="h-6 w-6 mr-2 text-purple-600" />
                Data Processing Principles
              </h2>
              
              <div className="space-y-6">
                <div className="border-l-4 border-purple-500 pl-4">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Lawfulness</h3>
                  <p className="text-gray-700">
                    We process personal data only when we have a lawful basis, such as consent, contract performance, 
                    or legitimate interests.
                  </p>
                </div>

                <div className="border-l-4 border-blue-500 pl-4">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Fairness and Transparency</h3>
                  <p className="text-gray-700">
                    We are transparent about how we collect, use, and share your personal data through clear privacy notices.
                  </p>
                </div>

                <div className="border-l-4 border-green-500 pl-4">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Purpose Limitation</h3>
                  <p className="text-gray-700">
                    We collect personal data only for specified, explicit, and legitimate purposes and do not process 
                    it in a manner incompatible with those purposes.
                  </p>
                </div>

                <div className="border-l-4 border-orange-500 pl-4">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Data Minimization</h3>
                  <p className="text-gray-700">
                    We collect only the personal data that is adequate, relevant, and limited to what is necessary 
                    for our purposes.
                  </p>
                </div>

                <div className="border-l-4 border-red-500 pl-4">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Accuracy</h3>
                  <p className="text-gray-700">
                    We take reasonable steps to ensure personal data is accurate and up-to-date, and we correct 
                    or delete inaccurate data.
                  </p>
                </div>

                <div className="border-l-4 border-indigo-500 pl-4">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Storage Limitation</h3>
                  <p className="text-gray-700">
                    We retain personal data only for as long as necessary to fulfill the purposes for which it was collected.
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <FaFileAlt className="h-6 w-6 mr-2 text-purple-600" />
                Your Data Protection Rights
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Right to Access</h3>
                  <p className="text-gray-700 text-sm">
                    You can request a copy of all personal data we hold about you.
                  </p>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Right to Rectification</h3>
                  <p className="text-gray-700 text-sm">
                    You can request correction of inaccurate or incomplete personal data.
                  </p>
                </div>

                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Right to Erasure</h3>
                  <p className="text-gray-700 text-sm">
                    You can request deletion of your personal data in certain circumstances.
                  </p>
                </div>

                <div className="bg-orange-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Right to Restrict Processing</h3>
                  <p className="text-gray-700 text-sm">
                    You can request limitation of how we process your personal data.
                  </p>
                </div>

                <div className="bg-red-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Right to Data Portability</h3>
                  <p className="text-gray-700 text-sm">
                    You can request transfer of your data to another service provider.
                  </p>
                </div>

                <div className="bg-indigo-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Right to Object</h3>
                  <p className="text-gray-700 text-sm">
                    You can object to processing of your personal data for certain purposes.
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Breach Response</h2>
              <p className="text-gray-700 mb-4">
                In the unlikely event of a data breach, we have established procedures to:
              </p>
              <ul className="list-disc pl-6 text-gray-700">
                <li>Immediately assess and contain the breach</li>
                <li>Notify relevant authorities within 72 hours</li>
                <li>Inform affected individuals without undue delay</li>
                <li>Conduct a thorough investigation and remediation</li>
                <li>Implement additional security measures to prevent recurrence</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Protection Officer</h2>
              <p className="text-gray-700 mb-4">
                We have appointed a Data Protection Officer (DPO) to oversee our data protection practices and ensure compliance.
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700"><strong>Contact our DPO:</strong></p>
                <p className="text-gray-700">Email: dpo@jhonks.com</p>
                <p className="text-gray-700">Phone: +234 (0) 800 123 4567</p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Third-Party Data Sharing</h2>
              <p className="text-gray-700 mb-4">
                We may share personal data with trusted third parties only when necessary and with appropriate safeguards:
              </p>
              <ul className="list-disc pl-6 text-gray-700">
                <li>Service providers bound by data protection agreements</li>
                <li>Legal authorities when required by law</li>
                <li>Business partners with your explicit consent</li>
                <li>In case of business transfers or mergers</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">International Data Transfers</h2>
              <p className="text-gray-700">
                When we transfer personal data outside Nigeria, we ensure appropriate safeguards are in place, 
                including standard contractual clauses and adequacy decisions by relevant authorities.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
              <p className="text-gray-700 mb-4">
                For any data protection inquiries or to exercise your rights, please contact us:
              </p>
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-gray-700"><strong>Email:</strong> privacy@jhonks.com</p>
                <p className="text-gray-700"><strong>Phone:</strong> +234 (0) 800 123 4567</p>
                <p className="text-gray-700"><strong>Address:</strong> 123 Innovation Street, Lagos, Nigeria</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataProtection;
